import React, { forwardRef, useRef, useEffect, useState, useCallback } from "react";
import { Tooltip } from "react-tooltip";

const TooltipWrapper = forwardRef(
  (
    {
      tooltipText,
      placement = "top",
      className = "",
      tooltipClassName = "",
      children,
      show = true,
      id = "tooltip-id",
      checkOverflow = false,
    },
    ref
  ) => {
    const childRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(true);

    // Check if a single element is overflowing
    const isElementOverflowing = useCallback((element) => {
      if (!element) return false;
      return element.scrollWidth > element.offsetWidth;
    }, []);

    // Check if any nested elements with truncation classes are overflowing
    const checkNestedElementsOverflow = useCallback(
      (containerElement) => {
        const nestedElements = containerElement.querySelectorAll(".text-truncate, .overflow-hidden");

        for (const element of nestedElements) {
          if (element && isElementOverflowing(element)) {
            return true;
          }
        }
        return false;
      },
      [isElementOverflowing]
    );

    // Main overflow checking function
    const checkElementOverflow = useCallback(() => {
      if (!childRef.current || !checkOverflow) return;

      requestAnimationFrame(() => {
        if (!childRef.current) return;

        const directlyOverflowing = isElementOverflowing(childRef.current);
        const nestedOverflowing = checkNestedElementsOverflow(childRef.current);

        setIsOverflowing(directlyOverflowing || nestedOverflowing);
      });
    }, [checkOverflow, isElementOverflowing, checkNestedElementsOverflow]);

    useEffect(() => {
      if (checkOverflow) {
        checkElementOverflow();
      } else {
        // If checkOverflow is false, always show tooltip
        setIsOverflowing(true);
      }
    }, [children, checkOverflow, checkElementOverflow]);

    // Re-check overflow when accordion might be expanded
    useEffect(() => {
      if (checkOverflow && childRef.current) {
        const observer = new MutationObserver(() => {
          checkElementOverflow();
        });

        // Observe changes in the parent accordion
        const accordionElement = childRef.current.closest(".accordion");
        if (accordionElement) {
          observer.observe(accordionElement, {
            attributes: true,
            subtree: true,
            attributeFilter: ["class", "aria-expanded"],
          });
        }

        return () => observer.disconnect();
      }
    }, [checkOverflow, checkElementOverflow]);

    if (!show) return <>{children}</>;

    if (!React.isValidElement(children)) {
      console.error("TooltipWrapper: children is not a valid React element");
      return null;
    }

    const enhancedChildren = React.cloneElement(children, {
      id,
      "data-tooltip-id": id,
      "data-tooltip-content": tooltipText,
      "data-tooltip-place": placement,
      className: `${children.props.className || ""} ${className}`,
      ref: childRef,
      // Re-check overflow on hover for accordion scenarios
      onMouseEnter: (e) => {
        if (checkOverflow) {
          checkElementOverflow();
        }
        // Call original onMouseEnter if it exists
        if (children.props.onMouseEnter) {
          children.props.onMouseEnter(e);
        }
      },
    });

    if (!isOverflowing) return <>{enhancedChildren}</>;

    return (
      <div>
        {enhancedChildren}
        <Tooltip
          id={id}
          className={`text-break-space text-break z-i-1080 ${tooltipClassName}`}
          appendTo={document.body}
          positionStrategy="fixed"
        />
      </div>
    );
  }
);

export default TooltipWrapper;
